import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MembershipUtils } from "../model/MembershipUtils";
import { hasActiveSubscription, PRO_MONTHLY_PRICE } from "../model/SubscriptionUtils";
import { GAUtils } from "../model/GAUtils";
import { useRewardedAd } from "./RewardedAd";
import { auth } from "../firebaseConfig";

const ADMIN_EMAILS = ['jasonlie7@gmail.com'];

const MembershipPrompt: React.FC = () => {
    const [isBlocked, setIsBlocked] = useState(false);
    const [adMessage, setAdMessage] = useState<string | null>(null);
    const navigate = useNavigate();

    const isAdmin = ADMIN_EMAILS.includes(auth.currentUser?.email || '');

    const handleAdReward = useCallback(() => {
        setAdMessage('Credit earned! You can continue searching.');
        // Dismiss prompt after a short delay since user now has a credit
        setTimeout(() => {
            setIsBlocked(false);
            setAdMessage(null);
        }, 1500);
    }, []);

    const handleAdError = useCallback((error: string) => {
        setAdMessage(error);
        setTimeout(() => setAdMessage(null), 3000);
    }, []);

    const { showRewardedAd, isLoading: adLoading } = useRewardedAd(handleAdReward, handleAdError);

    useEffect(() => {
        let isMounted = true;
        
        const checkCredits = async () => {
            // Skip prompt if user has active subscription
            const hasSub = await hasActiveSubscription();
            if (hasSub) {
                if (isMounted) setIsBlocked(false);
                return;
            }

            const isLimited = await MembershipUtils.isLimit();
            if (!isMounted) return;
            
            if (isLimited) {
                GAUtils.trackMembershipPromptView('out_of_credits');
                GAUtils.trackOutOfCredits('membership_prompt');
                setIsBlocked(true);
            } else {
                setIsBlocked(false);
            }
        };

        checkCredits();

        const handleCreditUpdate = () => checkCredits();
        window.addEventListener('credits-updated', handleCreditUpdate);

        return () => {
            isMounted = false;
            window.removeEventListener('credits-updated', handleCreditUpdate);
        };
    }, []);

    const handleBuyCredits = () => {
        GAUtils.trackMembershipPromptClick('buy_credits');
        navigate('/balance?amount=5');
    };

    const handleGoPro = () => {
        GAUtils.trackMembershipPromptClick('go_pro');
        navigate('/balance?tab=pro');
    };

    if (!isBlocked) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.9)', color: '#fff', zIndex: 1000,
            overflowY: 'auto', padding: '20px', boxSizing: 'border-box',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
        }}>
            <div style={{
                maxWidth: '480px', width: '100%', padding: '32px', borderRadius: '16px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                <h1 style={{
                    marginBottom: '12px', fontSize: '24px', fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>You've Used All Your Free Searches</h1>
                <p style={{ marginBottom: '24px', fontSize: '16px', lineHeight: '1.6', color: '#94a3b8' }}>
                    Keep searching Telegram channels, groups & bots —<br />
                    go Pro for unlimited access or top up in seconds.
                </p>

                {/* Pro Plan CTA */}
                <div style={{
                    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    borderRadius: '12px', padding: '16px', marginBottom: '16px',
                }}>
                    <div style={{ fontSize: '14px', color: '#fbbf24', fontWeight: 600, marginBottom: '4px' }}>
                        ⭐ RECOMMENDED
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '28px', fontWeight: 700, color: '#fff' }}>${PRO_MONTHLY_PRICE}</span>
                        <span style={{ fontSize: '14px', color: '#94a3b8' }}>/month</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#fbbf24', marginTop: '4px' }}>
                        Unlimited searches · No ads · Cancel anytime
                    </div>
                </div>

                <button
                    onClick={handleGoPro}
                    style={{
                        padding: '14px 32px', fontSize: '16px', fontWeight: 600,
                        border: 'none', borderRadius: '10px',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: '#fff', cursor: 'pointer', width: '100%', maxWidth: '280px',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        marginBottom: '12px',
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 20px rgba(245, 158, 11, 0.3)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >⭐ Go Pro — Unlimited</button>

                <div style={{ color: '#64748b', fontSize: '13px', margin: '8px 0 16px' }}>— or —</div>

                {/* Watch Ad option — admin only */}
                {isAdmin && (<>
                <div style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '12px', padding: '12px', marginBottom: '12px',
                }}>
                    <div style={{ fontSize: '13px', color: '#10b981', fontWeight: 600, marginBottom: '4px' }}>
                        FREE
                    </div>
                    <div style={{ fontSize: '14px', color: '#94a3b8' }}>
                        Watch a short ad to earn 1 credit
                    </div>
                </div>

                <button
                    onClick={() => { GAUtils.trackMembershipPromptClick('watch_ad'); showRewardedAd(); }}
                    disabled={adLoading}
                    style={{
                        padding: '12px 28px', fontSize: '14px', fontWeight: 600,
                        border: '1px solid rgba(16, 185, 129, 0.5)', borderRadius: '10px',
                        background: 'transparent', color: '#10b981', cursor: adLoading ? 'not-allowed' : 'pointer',
                        width: '100%', maxWidth: '280px',
                        transition: 'transform 0.2s ease',
                        marginBottom: '12px',
                        opacity: adLoading ? 0.6 : 1,
                    }}
                    onMouseOver={(e) => { if (!adLoading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >{adLoading ? '⏳ Loading Ad...' : '🎬 Watch Ad — Free Credit'}</button>

                {adMessage && (
                    <div style={{ fontSize: '13px', color: adMessage.includes('earned') ? '#10b981' : '#ef4444', marginBottom: '8px' }}>
                        {adMessage}
                    </div>
                )}

                <div style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 12px' }}>— or —</div>
                </>)}

                {/* Credits option */}
                <div style={{
                    background: 'rgba(102, 126, 234, 0.1)', borderRadius: '12px',
                    padding: '12px', marginBottom: '16px',
                }}>
                    <div style={{ fontSize: '13px', color: '#667eea', fontWeight: 600, marginBottom: '6px' }}>
                        💳 TOP UP — NO SUBSCRIPTION
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '28px', fontWeight: 700, color: '#fff' }}>$5</span>
                        <span style={{ fontSize: '14px', color: '#94a3b8' }}>= 100 searches</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        from $1 · no expiry
                    </div>
                </div>

                <button
                    onClick={handleBuyCredits}
                    style={{
                        padding: '12px 28px', fontSize: '14px', fontWeight: 600,
                        border: '1px solid rgba(102, 126, 234, 0.5)', borderRadius: '10px',
                        background: 'transparent', color: '#667eea', cursor: 'pointer',
                        width: '100%', maxWidth: '280px',
                        transition: 'transform 0.2s ease',
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >💳 Top Up Credits</button>

            </div>
        </div>
    );
};

export default MembershipPrompt;
